import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { api, formatApiError } from "@/lib/api";

const RANGES = [[30, "30 days"], [90, "90 days"], [180, "6 months"], [365, "12 months"]];
const OUTCOMES = {
  verified: ["Verified", "#059669"], discrepant: ["Discrepancy found", "#D97706"], unverifiable: ["Could not verify", "#6B7280"],
  in_progress: ["In progress", "#2F4BE0"], no_consent: ["No consent", "#DC2626"], cancelled: ["Cancelled", "#CBD5E1"],
};
const money = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const weekLabel = (w) => new Date(`${w}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const Kpi = ({ id, label, value, hint }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`kpi-${id}`}>
    <div className="font-mono-label text-[9px] font-semibold text-oath-muted">{label}</div>
    <div className="mt-2 font-display text-2xl font-bold text-oath-ink sm:text-3xl" data-testid={`kpi-${id}-value`}>{value}</div>
    {hint && <div className="mt-1 text-xs text-oath-secondary">{hint}</div>}
  </div>
);

const Panel = ({ title, children, testid }) => (
  <div className="rounded-3xl border border-gray-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-display text-base font-bold text-oath-ink md:text-lg">{title}</h2>
    <div className="mt-4">{children}</div>
  </div>
);

const VolumeChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data.map((d) => ({ ...d, label: weekLabel(d.week) }))} margin={{ left: -20, right: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} interval="preserveStartEnd" />
      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} />
      <Tooltip cursor={{ fill: "#EFF2FE" }} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="opened" name="Opened" fill="#2F4BE0" radius={[4, 4, 0, 0]} />
      <Bar dataKey="finished" name="Delivered" fill="#059669" radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const OutcomeChart = ({ outcomes }) => {
  const rows = Object.entries(outcomes).map(([k, v]) => ({ key: k, name: OUTCOMES[k]?.[0] || k, value: v }));
  if (!rows.length) return <p className="py-16 text-center text-sm text-oath-muted" data-testid="outcomes-empty">No cases in this period.</p>;
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <ResponsiveContainer width="100%" height={200} className="sm:max-w-[200px]">
        <PieChart><Pie data={rows} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
          {rows.map((r) => <Cell key={r.key} fill={OUTCOMES[r.key]?.[1] || "#94A3B8"} />)}
        </Pie><Tooltip /></PieChart>
      </ResponsiveContainer>
      <ul className="w-full space-y-2 text-sm">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center justify-between gap-3" data-testid={`outcome-${r.key}`}>
            <span className="flex items-center gap-2 text-oath-secondary"><span className="h-2.5 w-2.5 rounded-full" style={{ background: OUTCOMES[r.key]?.[1] }} />{r.name}</span>
            <span className="font-mono text-xs text-oath-ink">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Toolbar = ({ days, setDays, orgs, orgId, setOrgId, onExport, exporting }) => (
  <div className="mb-6 flex flex-wrap items-center gap-2">
    <div className="flex rounded-full border border-gray-200 bg-white p-1" data-testid="analytics-range">
      {RANGES.map(([d, l]) => (
        <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
          className={`rounded-full px-3 py-1.5 text-xs transition-colors ${days === d ? "bg-oath-ink text-white" : "text-oath-secondary hover:text-oath-ink"}`}>{l}</button>
      ))}
    </div>
    {orgs && (
      <select value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="analytics-org-filter"
        className="rounded-full border border-gray-200 bg-white px-4 py-2 text-xs text-oath-ink">
        <option value="">All businesses</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    )}
    <button onClick={onExport} disabled={exporting} data-testid="analytics-export-button"
      className="ml-auto inline-flex items-center gap-2 rounded-full bg-oath-ink px-4 py-2 font-mono-label text-[10px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
      {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} EXPORT CSV
    </button>
  </div>
);

const useAnalytics = (endpoint, days, orgId) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(endpoint, { params: { days, ...(orgId ? { org_id: orgId } : {}) } })
      .then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  }, [endpoint, days, orgId]);
  return data;
};

export const AnalyticsView = ({ endpoint, admin = false }) => {
  const [days, setDays] = useState(90);
  const [orgId, setOrgId] = useState("");
  const [orgs, setOrgs] = useState(admin ? [] : null);
  const [exporting, setExporting] = useState(false);
  const data = useAnalytics(endpoint, days, orgId);
  useEffect(() => {
    if (admin && data?.orgs) {
      setOrgs(data.orgs);
    }
  }, [admin, data]);

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`${endpoint}/export`, { params: { days, ...(orgId ? { org_id: orgId } : {}) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `residenceproof-report-${days}d.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="analytics-view">
      <Toolbar {...{ days, setDays, orgs, orgId, setOrgId, onExport, exporting }} />
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-oath-muted" data-testid="analytics-loading" /> : <AnalyticsBody data={data} />}
    </div>
  );
};

const AnalyticsBody = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Kpi id="total" label="CASES OPENED" value={data.total} hint={`${data.delivered} delivered · ${data.in_progress} open`} />
      <Kpi id="turnaround" label="AVG. TURNAROUND" value={data.avg_turnaround_days == null ? "—" : `${data.avg_turnaround_days} d`} hint="opened to delivered" />
      <Kpi id="consent" label="CONSENT RATE" value={data.consent_rate == null ? "—" : `${data.consent_rate}%`} hint="of subjects who responded" />
      <Kpi id="credits" label="CREDITS USED" value={data.credits_used} hint={`${data.credits_left} left`} />
    </div>
    <div className="grid gap-4 lg:grid-cols-5">
      <div className="lg:col-span-3"><Panel title="Case volume by week" testid="analytics-volume-chart"><VolumeChart data={data.weekly} /></Panel></div>
      <div className="lg:col-span-2"><Panel title="Outcomes" testid="analytics-outcomes"><OutcomeChart outcomes={data.outcomes} /></Panel></div>
    </div>
    <Panel title="Spend" testid="analytics-spend">
      <div className="flex flex-wrap gap-8 text-sm">
        <div><div className="font-mono-label text-[9px] font-semibold text-oath-muted">PAID</div><div className="mt-1 font-display text-2xl font-bold text-oath-ink" data-testid="spend-amount">{money(data.spend.amount)}</div></div>
        <div><div className="font-mono-label text-[9px] font-semibold text-oath-muted">PAYMENTS</div><div className="mt-1 font-display text-2xl font-bold text-oath-ink" data-testid="spend-count">{data.spend.payments}</div></div>
        <div><div className="font-mono-label text-[9px] font-semibold text-oath-muted">COST PER CASE</div><div className="mt-1 font-display text-2xl font-bold text-oath-ink" data-testid="spend-per-case">{data.credits_used && data.spend.amount ? money(data.spend.amount / data.credits_used) : "—"}</div></div>
      </div>
    </Panel>
  </div>
);
